import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { platformWorkAPI } from '../../services/api';

const slugify = (label) => label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export const WorkbackStagesDialog = ({ open, onOpenChange, stages, currentUser, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setRows((stages || []).map(s => ({ ...s })));
  }, [open, stages]);

  const updateLabel = (idx, label) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, label, value: r.value || slugify(label) } : r));
  };
  const addRow = () => setRows(prev => [...prev, { value: '', label: '' }]);
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    setRows(prev => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    const cleaned = rows
      .map(r => ({ label: (r.label || '').trim(), value: r.value || slugify(r.label || '') }))
      .filter(r => r.label && r.value);
    if (cleaned.length === 0) { toast.error('Add at least one stage'); return; }
    const values = cleaned.map(r => r.value);
    if (new Set(values).size !== values.length) { toast.error('Stage names must be unique'); return; }
    setSaving(true);
    try {
      await platformWorkAPI.updateWorkback(cleaned, currentUser?.email || currentUser?.name || null);
      toast.success('Workback stages updated');
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save stages');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="workback-stages-dialog">
        <DialogHeader>
          <DialogTitle>LevelUp Workback Stages</DialogTitle>
          <DialogDescription>Ordered stages that drive the status of every Project & Task. Reorder with the arrows.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((r, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`stage-row-${idx}`}>
              <span className="text-xs text-muted-foreground w-4 text-right">{idx + 1}</span>
              <Input value={r.label} onChange={e => updateLabel(idx, e.target.value)} placeholder="Stage name" data-testid={`stage-input-${idx}`} />
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, -1)} disabled={idx === 0}><ArrowUp className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, 1)} disabled={idx === rows.length - 1}><ArrowDown className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeRow(idx)} data-testid={`stage-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} data-testid="stage-add-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Stage</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="stages-save-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">{saving ? 'Saving...' : 'Save Stages'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkbackStagesDialog;
